import React, { useEffect, useState, useRef } from 'react';

/**
 * BreathingHalo - Halo circulaire progressif dynamique pour exercices PREMIUM
 * 
 * Affiche un cercle de progression qui se remplit pendant chaque phase de respiration
 * Synchronisé avec la durée exacte de chaque phase (inhale, hold, exhale)
 */
const BreathingHalo = ({ phase, phaseDuration, isActive }) => {
  const [progress, setProgress] = useState(0);
  const animationRef = useRef(null);
  const phaseStartTimeRef = useRef(null);
  const currentPhaseRef = useRef(phase);

  // Réinitialiser à chaque changement de phase
  useEffect(() => {
    if (currentPhaseRef.current !== phase) {
      currentPhaseRef.current = phase;
      phaseStartTimeRef.current = Date.now();
      setProgress(0);
    }
  }, [phase]);

  // Animation du halo progressif
  useEffect(() => {
    if (!isActive || !phaseDuration) {
      setProgress(0);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    // Initialiser le temps de départ si nécessaire
    if (!phaseStartTimeRef.current) {
      phaseStartTimeRef.current = Date.now();
    }

    const animate = () => {
      const elapsed = (Date.now() - phaseStartTimeRef.current) / 1000;
      const currentProgress = Math.min((elapsed / phaseDuration) * 100, 100);
      
      setProgress(currentProgress);

      // Continuer l'animation tant qu'on n'a pas atteint 100%
      if (currentProgress < 100 && isActive) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, phaseDuration, phase]);

  if (!isActive) return null;

  // Calcul du cercle SVG
  const size = 190; // Taille du halo (plus grand que le cercle principal)
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div 
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
      style={{ width: '100%', height: '100%' }}
    >
      <svg
        width={size}
        height={size}
        style={{
          transform: 'rotate(-90deg)', // Commence en haut
          filter: 'drop-shadow(0 0 15px rgba(159, 124, 255, 0.45))',
        }}
      >
        {/* Cercle de fond (optionnel, très subtil) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(159, 124, 255, 0.1)"
          strokeWidth={strokeWidth}
        />
        
        {/* Cercle de progression */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="url(#haloGradient)"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transition: 'none', // Pas de transition CSS, on utilise RAF
          }}
        />
        
        {/* Gradient violet premium */}
        <defs>
          <linearGradient id="haloGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#9F7CFF" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#A57BFF" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#9F7CFF" stopOpacity="0.9" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default BreathingHalo;
